/**
 * Presentational table for the Universe tab's merged rows.
 *
 * Pure-presentational: it renders the `UniverseRow[]` produced by
 * `mergeUniverse` (UniverseTab.tsx) and emits owner actions back to the parent
 * via callbacks. It owns no fetching, no mutation, and no merge logic — the
 * parent decides what `canWrite` gates and what each action does, so this stays
 * trivially testable and the merge math lives in one pure place.
 *
 * Columns: symbol · sector (pool) · membership chips · qty · last value ·
 * next earnings (with a blackout flag) · pool actions.
 *
 * Styling is deferred to Task 8; only semantic classNames are emitted here.
 */

import type { UniverseRow } from '../tabs/UniverseTab';

export interface SymbolTableProps {
  rows: UniverseRow[];
  /** Owner actions are rendered only when true. */
  canWrite: boolean;
  /** Remove a symbol from the pool (full-list PUT handled by the parent). */
  onRemoveFromPool: (symbol: string) => void;
  /** Open the watchlist editor prefilled to promote this symbol. */
  onPromoteToWatchlist: (symbol: string) => void;
  /** A pool write is in flight — disable pool actions to avoid double-fires. */
  busy?: boolean;
}

/** Compact numeric render; `null`/`undefined` → em dash. */
function fmtNum(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return String(value);
}

/** Compact currency-ish render for last value; `null` → em dash. */
function fmtValue(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function SymbolTable({
  rows,
  canWrite,
  onRemoveFromPool,
  onPromoteToWatchlist,
  busy,
}: SymbolTableProps) {
  return (
    <table className="symbol-table">
      <thead>
        <tr>
          <th scope="col">Symbol</th>
          <th scope="col">Sector</th>
          <th scope="col">Membership</th>
          <th scope="col">Qty</th>
          <th scope="col">Last value</th>
          <th scope="col">Next earnings</th>
          {canWrite && <th scope="col">Pool</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr className="symbol-row" key={row.symbol} data-symbol={row.symbol}>
            <td className="cell-symbol">{row.symbol}</td>
            <td className="cell-sector">{row.sector ?? '—'}</td>
            <td className="cell-chips">
              {row.inPosition && <span className="chip chip-position">position</span>}
              {row.inPool && <span className="chip chip-pool">pool</span>}
              {row.inWatchlist && <span className="chip chip-watchlist">watchlist</span>}
            </td>
            <td className="cell-qty">{fmtNum(row.qty)}</td>
            <td className="cell-value">{fmtValue(row.lastValue)}</td>
            <td className="cell-earnings">
              {row.nextEarnings ?? '—'}
              {row.earningsBlackout && (
                <span className="flag flag-blackout" title="Earnings inside the blackout window">
                  {' '}
                  ⚠ blackout
                </span>
              )}
            </td>
            {canWrite && (
              <td className="cell-actions">
                {row.inPool && (
                  <button
                    type="button"
                    className="btn btn-pool-remove"
                    data-symbol={row.symbol}
                    disabled={busy}
                    onClick={() => onRemoveFromPool(row.symbol)}
                  >
                    Remove from pool
                  </button>
                )}
                {!row.inWatchlist && (
                  <button
                    type="button"
                    className="btn btn-promote"
                    data-symbol={row.symbol}
                    disabled={busy}
                    onClick={() => onPromoteToWatchlist(row.symbol)}
                  >
                    Promote to watchlist
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
